import type { MetricInfo, StepPlanInfoId } from '@az3fr/core';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { BadgeSwitch } from '@/components/ui/badge-switch';
import { ScrollableRow, type ScrollableRowRef } from '@/components/ui/scrollable-row';
import { metricSliceSelectors } from '@/entities/metrics/model/metric.selector';
import { stepPlanSliceSelectors } from '@/entities/step-plan/model/step-plan.selector';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { isTrackedMetric } from '@/features/workout-creator/ui/section-editor.utils';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';
import { useFlashEffect } from '@/hooks/use-flash-effect.hook';

interface SelectableMetricListProps {
	/** The step plan ID to manage metrics for */
	stepPlanId: StepPlanInfoId;
	/** Maximum number of metrics that can be selected */
	maxSelection?: number;
	/** Whether to show the max selection indicator */
	showMaxIndicator?: boolean;
	/** Custom label for the section */
	label?: string;
	/** Additional CSS classes for the container */
	className?: string;
}

/**
 * SelectableMetricList Component
 *
 * A reusable component that displays metrics in a horizontal scrollable row
 * with toggleable badge switches. Users can select/deselect metrics with
 * visual feedback and optional maximum selection limits. This component
 * manages its own state through Redux selectors and thunks.
 *
 * Features:
 * - Horizontal scrolling through all available metrics
 * - Toggleable badge switches for each metric
 * - Optional maximum selection limit with indicator
 * - Customizable label and styling
 * - Automatic state management via Redux
 * - Self-contained metric selection logic
 *
 * @param props - Component props
 * @returns JSX element representing the selectable metric list
 */
function SelectableMetricList({
	stepPlanId,
	maxSelection = 3,
	showMaxIndicator = true,
	label = 'Properties',
	className = '',
}: SelectableMetricListProps) {
	const dispatch = useAppDispatch();
	const { isFlashing, triggerFlash } = useFlashEffect();
	const scrollableRowRef = useRef<ScrollableRowRef>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// Get all available metrics
	const allMetrics = useAppSelector(metricSliceSelectors.selectAll);

	// Get currently tracked metrics for this step plan
	const trackedMetrics = useAppSelector((state) =>
		stepPlanSliceSelectors.selectPopulatedTrackedMetricsByStepPlanId(state, stepPlanId),
	);

	// Sort metrics to show selected ones first
	const sortedMetrics = [...allMetrics].sort((a, b) => {
		const aIsSelected = isTrackedMetric(trackedMetrics, a.id);
		const bIsSelected = isTrackedMetric(trackedMetrics, b.id);

		if (aIsSelected && !bIsSelected) {
			return -1;
		}
		if (!aIsSelected && bIsSelected) {
			return 1;
		}
		return 0;
	});

	const handleMetricToggle = (metric: MetricInfo) => {
		const isCurrentlySelected = isTrackedMetric(trackedMetrics, metric.id);

		if (isCurrentlySelected) {
			// Remove the metric from tracking
			dispatch(
				workoutCreatorThunks.removeTrackedMetricFromStepPlan({
					stepPlanId,
					metricCanonicalName: metric.canonicalName,
				}),
			);
		} else {
			// Check if we would exceed the maximum selection limit
			if (maxSelection && trackedMetrics.length >= maxSelection) {
				triggerFlash(); // Flash red when trying to exceed limit
				return;
			}
			// Add the metric to tracking
			dispatch(
				workoutCreatorThunks.addTrackedMetricToStepPlan({
					stepPlanId,
					metricCanonicalName: metric.canonicalName,
				}),
			);
		}
	};

	const handleMetricClick = (metric: MetricInfo) => {
		const isCurrentlySelected = isTrackedMetric(trackedMetrics, metric.id);
		const isDisabled = maxSelection
			? trackedMetrics.length >= maxSelection && !isCurrentlySelected
			: false;

		// If disabled and trying to select, trigger flash
		if (isDisabled && !isCurrentlySelected) {
			triggerFlash();
			return;
		}

		// Otherwise, proceed with normal toggle
		handleMetricToggle(metric);
	};

	const handleScrollStateChange = useCallback((left: boolean, right: boolean) => {
		setCanScrollLeft(left);
		setCanScrollRight(right);
	}, []);

	const handleScrollLeft = useCallback(() => {
		scrollableRowRef.current?.scrollLeft();
	}, []);

	const handleScrollRight = useCallback(() => {
		scrollableRowRef.current?.scrollRight();
	}, []);

	return (
		<div className={`space-y-1.5 ${className}`}>
			{/*--- Section Header with Navigation ---*/}
			<div className="flex items-center justify-between">
				<span className="flex items-center gap-1.5 sm:gap-2">
					<span className="font-medium text-gray-500 text-xs uppercase tracking-widest">
						{label}
					</span>
					{showMaxIndicator && maxSelection && (
						<span
							className={`rounded-full px-1.5 py-0.5 font-semibold text-[0.65rem] capitalize transition-colors duration-200 sm:px-2 sm:text-xs ${
								isFlashing ? 'animate-pulse bg-red-200 text-red-700' : 'bg-gray-100 text-gray-500'
							}`}
						>
							max {maxSelection}
						</span>
					)}
				</span>

				{/*--- Custom Navigation Buttons ---*/}
				{(canScrollLeft || canScrollRight) && (
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={handleScrollLeft}
							disabled={!canScrollLeft}
							className="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:bg-white"
							aria-label="Scroll left"
						>
							<ChevronLeftIcon className="size-4" />
						</button>
						<button
							type="button"
							onClick={handleScrollRight}
							disabled={!canScrollRight}
							className="flex size-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:bg-white"
							aria-label="Scroll right"
						>
							<ChevronRightIcon className="size-4" />
						</button>
					</div>
				)}
			</div>

			{/*--- Scrollable Metrics Row ---*/}
			<div className="w-full overflow-hidden">
				<ScrollableRow
					ref={scrollableRowRef}
					className="w-full"
					onScrollStateChange={handleScrollStateChange}
				>
					{sortedMetrics.map((metric) => {
						const isSelected = isTrackedMetric(trackedMetrics, metric.id);
						const isDisabled = maxSelection
							? trackedMetrics.length >= maxSelection && !isSelected
							: false;

						return (
							<BadgeSwitch
								key={metric.id}
								label={metric.nameAbbreviation || metric.name}
								subLabel={metric.unitAbbreviation || undefined}
								checked={isSelected}
								disabled={false} // Never disable to allow click capture
								onChange={() => handleMetricClick(metric)}
								className={isDisabled ? 'cursor-not-allowed opacity-50' : ''}
							/>
						);
					})}
				</ScrollableRow>
			</div>
		</div>
	);
}

export { SelectableMetricList };
